import React, { useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { EvmChainId, networkPath, tokenPath, walletPath } from '../utils/chains';
import Header from './Header';
import Footer from './Footer';
import TierGate from './TierGate';
import { apiFetch, getApiHost } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { hasWatcherAccess } from '../utils/tiers';

// Cosmograph pulls in WebGL + GPU layout code (~1MB). Lazy-load so the rest
// of the SPA isn't slowed by it.
const HolderGraphCanvas = lazy(() => import('./HolderGraphCanvas'));

export interface HolderGraphNode {
  id: string;
  type: 'token' | 'network' | 'holder';
  label: string;
  // type-specific fields:
  symbol?: string;
  name?: string;
  image?: string | null;
  walletAddress?: string;
  percentage?: number;
  networkAddress?: string | null;
  topFundingSource?: string | null;
  exchange?: string | null;
  tags?: string[];
  isLiquidityPool?: boolean;
  hasLockedSupply?: boolean;
  hasStakedSupply?: boolean;
  address?: string;
  walletCount?: number;
  percentOwnership?: number;
  riskLevel?: string | null;
}

export interface HolderGraphEdge {
  source: string;
  target: string;
  type: 'holds' | 'funded-via' | 'members-hold';
}

export interface HolderGraphResponse {
  mint: string;
  symbol: string;
  timestamp: string;
  counts: {
    nodes: number;
    edges: number;
    holdersShown: number;
    holdersResearched: number;
    holdersTotal: number | null;
    networks: number;
    /** @deprecated use holdersShown */
    holders?: number;
  };
  nodes: HolderGraphNode[];
  edges: HolderGraphEdge[];
}

const HolderGraphPage: React.FC<{ chain?: EvmChainId }> = ({ chain }) => {
  const { mint } = useParams<{ mint: string }>();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [unlockChecked, setUnlockChecked] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !mint) { setUnlockChecked(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${getApiHost()}/api/research-unlock/${mint}`);
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          setUnlocked(!!data.unlocked);
        }
      } finally {
        if (!cancelled) setUnlockChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, isAuthenticated, mint]);

  if (authLoading || (isAuthenticated && !unlockChecked)) {
    return (
      <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-crypto-accent border-t-transparent rounded-full" />
        </main>
        <Footer />
      </div>
    );
  }
  if (!hasWatcherAccess(user?.tier) && !unlocked) {
    return (
      <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
        <Header />
        <main className="flex-1">
          <TierGate
            required="watcher"
            feature="the holder graph"
            mint={mint}
            onUnlock={() => setUnlocked(true)}
          />
        </main>
        <Footer />
      </div>
    );
  }
  return <HolderGraphPageContent chain={chain} />;
};

const HolderGraphPageContent: React.FC<{ chain?: EvmChainId }> = ({ chain }) => {
  const { mint } = useParams<{ mint: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<HolderGraphResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!mint) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const resp = await apiFetch(`${getApiHost()}/api/token/${mint}/holder-graph${chain ? `?chain=${chain}` : ''}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mint, chain]);

  const handleNodeClick = useMemo(() => (node: HolderGraphNode) => {
    const addr = node.type === 'holder' ? node.walletAddress : node.type === 'network' ? node.address : null;
    if (!addr) return; // token clicks: no-op (already on its page)
    navigate(node.type === 'holder' ? walletPath(addr, chain) : networkPath(addr, chain));
  }, [navigate, chain]);

  return (
    <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-6 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold flex items-baseline gap-3">
              {data?.symbol ? <span>${data.symbol}</span> : <span className="text-gray-500">Loading…</span>}
              <span className="text-base text-gray-400 font-normal">Holder network</span>
            </h1>
            {data && (
              <p className="text-xs text-gray-500 mt-1">
                Showing top {data.counts.holdersShown.toLocaleString()}
                {data.counts.holdersTotal != null && (
                  <> of <span className="text-gray-300">{data.counts.holdersTotal.toLocaleString()}</span> holders</>
                )}
                {' '}(researched {data.counts.holdersResearched.toLocaleString()}) ·{' '}
                {data.counts.networks} funding networks ·{' '}
                {data.counts.edges} edges
              </p>
            )}
          </div>
          {mint && (
            <Link
              to={tokenPath(mint, chain)}
              className="text-sm text-crypto-accent hover:text-crypto-accent/80 transition-colors"
            >
              ← Back to token
            </Link>
          )}
        </div>

        <div className="flex-1 min-h-[600px] bg-black/40 border border-gray-800 rounded-xl overflow-hidden relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
              <i className="fas fa-spinner fa-spin mr-2"></i> Loading graph…
            </div>
          )}
          {error && !loading && (
            <div className="absolute inset-0 flex items-center justify-center text-red-400 text-sm">
              Failed to load graph: {error}
            </div>
          )}
          {data && !loading && !error && (
            <Suspense fallback={
              <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
                <i className="fas fa-spinner fa-spin mr-2"></i> Loading graph engine…
              </div>
            }>
              <HolderGraphCanvas data={data} onNodeClick={handleNodeClick} />
            </Suspense>
          )}
        </div>

        {data && (
          <div className="mt-4 flex flex-wrap gap-4 text-xs text-gray-500">
            <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-white mr-1.5 align-middle"></span>token</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5 align-middle" style={{ background: 'rgb(55,85,190)' }}></span>funding network</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-red-400 mr-1.5 align-middle"></span>risky network</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-400 mr-1.5 align-middle"></span>holder</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-green-400 mr-1.5 align-middle"></span>locked</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-400 mr-1.5 align-middle"></span>staked</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-red-400 mr-1.5 align-middle"></span>risky holder (rugger-tagged or ≥10% of supply)</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-pink-400 mr-1.5 align-middle"></span>KOL (GMGN)</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-teal-400 mr-1.5 align-middle"></span>smart money (GMGN)</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-orange-400 mr-1.5 align-middle"></span>FOMO trader</span>
            <span className="ml-auto text-gray-600">Click a holder or network to drill in.</span>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default HolderGraphPage;
